// Who is signed in and what the door says about them. Three answers
// besides loading: no Firebase user (show sign-in), a Google account
// the door refuses with 403 not_registered (the "kayıtlı değil"
// screen), or a registered person with their `Me`.

import {useEffect, useState} from "react";
import {onAuthStateChanged, type User} from "firebase/auth";
import {ApiRequestError, getMe} from "../api/client";
import type {Me} from "../api/contract";
import {auth, signOutEverywhere} from "../firebase";

export type Session =
  | {state: "loading"}
  | {state: "signed_out"}
  | {state: "not_registered"; email: string}
  | {state: "error"; message: string; retry: () => void}
  | {state: "ready"; user: User; me: Me};

type Outcome =
  | {kind: "ready"; me: Me}
  | {kind: "not_registered"; email: string}
  | {kind: "error"; message: string};

export function useSession(): Session {
  const [user, setUser] = useState<User | null | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  // The door's answer for (user, attempt); anything else is loading.
  const [outcome, setOutcome] =
    useState<{uid: string; attempt: number; value: Outcome} | null>(null);

  useEffect(() => onAuthStateChanged(auth, (u) => setUser(u)), []);

  useEffect(() => {
    if (!user) return;
    const ctl = new AbortController();
    const uid = user.uid;
    const email = user.email ?? "";
    getMe(ctl.signal).then(({data}) => {
      setOutcome({uid, attempt, value: {kind: "ready", me: data}});
    }).catch(async (e: unknown) => {
      if (ctl.signal.aborted) return;
      if (e instanceof ApiRequestError && e.code === "not_registered") {
        setOutcome({uid, attempt, value: {kind: "not_registered", email}});
        return;
      }
      if (e instanceof ApiRequestError && e.status === 401) {
        // The token did not pass even after a refresh — start over.
        await signOutEverywhere();
        return;
      }
      setOutcome({uid, attempt, value: {
        kind: "error", message: e instanceof Error ? e.message : String(e),
      }});
    });
    return () => ctl.abort();
  }, [user, attempt]);

  if (user === undefined) return {state: "loading"};
  if (user === null) return {state: "signed_out"};
  if (!outcome || outcome.uid !== user.uid || outcome.attempt !== attempt) {
    return {state: "loading"};
  }
  const v = outcome.value;
  switch (v.kind) {
  case "ready": return {state: "ready", user, me: v.me};
  case "not_registered": return {state: "not_registered", email: v.email};
  case "error": return {
    state: "error", message: v.message,
    retry: () => setAttempt((n) => n + 1),
  };
  }
}
